import { Button, Dialog, DialogContent, Grid, useTheme, lighten } from "@mui/material";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TravelExpenseDTO } from "src/models/travel-expenses/travel-expense-dto";
import AuditCommentsBody from "./AuditCommentsBody/AuditCommentsBody";
import { State, Props } from "./interfaces";
import ConfirmationChangesDialog from "../../../../../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../../../../../../containers/LoadingOverlay/LoadingOverlay";
import { DocumentDTO } from "../../../../../../../../../models/documents";
import { TravelExpenseAuditNotesDTO } from "../../../../../../../../../models/travel-expense-audit-notes";
import { TravelExpenseSTO } from "../../../../../../../../../models/travel-expenses/travel-expense-sto";
import { TravelExpensesApiService } from "../../../../../../../../../services/travel-expenses/travel-expenses-api.service";
import { toastifyResponse } from "../../../../../../../../../utils/toastify-response/toastify-response";

const AuditComments = ({
  item,
  handleNotifyParent,
  employee,
  readOnly = false,
  children,
  handleUpdateAuditActions,
  handleUpdateExportAuditNotes,
}: Props) => {
  const tRP = "components.add_edit_travel_expense";
  const { t } = useTranslation();

  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      travelExpense: item,
      isCCDOpen: false,
      areFieldsValidated: true,
      error: "",
      auditActionsMutated: false,
    };
  }, [item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeField = useCallback(
    (field: keyof TravelExpenseDTO, value: TravelExpenseDTO[keyof TravelExpenseDTO]) => {
      setState((state) => ({
        ...state,
        travelExpense: {
          ...state.travelExpense,
          [field]: value,
          ...(field === "acceptedAmount" && {
            difference: state.travelExpense.amount - Number(value),
          }),
          ...(field === "vatRate" && {
            vatAmount: Number(value) * state.travelExpense.amount,
          }),
          ...(field === "vatAmount" && {
            vatRate: state.travelExpense.amount / Number(value),
          }),
        },
      }));
    },
    []
  );

  useEffect(() => {
    if (state.isDialogOpen && item) {
      setState((state) => ({
        ...state,
        travelExpense: item,
      }));
    }
  }, [state.isDialogOpen, item]);

  const handleSave = useCallback(
    async (andAuditAccept?: boolean | null) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const response = await new TravelExpensesApiService().saveTravelExpenseAuditDetails([
          {
            ...state.travelExpense,
            ...(andAuditAccept !== null &&
              andAuditAccept !== undefined && {
                auditAccepted: andAuditAccept,
              }),
          } as TravelExpenseSTO,
        ]);

        if (!response.error) {
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            travelExpense: item,
            isCCDOpen: false,
            areFieldsValidated: true,
            error: "",
            auditActionsMutated: false,
          }));

          handleNotifyParent && handleNotifyParent();
        } else {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        }

        toastifyResponse(response);
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [INITIAL_STATE, handleNotifyParent, state.travelExpense]
  );

  const handleDiscardChanges = useCallback(() => {
    // const auditDoc = state.travelExpense.auditDocument;
    const actualAuditNotes = state.travelExpense.auditNotes;

    state.auditActionsMutated &&
      handleUpdateAuditActions &&
      handleUpdateAuditActions(state.travelExpense.id, actualAuditNotes);

    setState({ ...INITIAL_STATE });
  }, [
    INITIAL_STATE,
    state.auditActionsMutated,
    // state.travelExpense.auditDocument,
    state.travelExpense.id,
    handleUpdateAuditActions,
  ]);

  const resetAndCloseDialog = (): void => {
    const travelExpenseToCompare = {
      ...state.travelExpense,
      // auditDocument: INITIAL_STATE.travelExpense.auditDocument,
      auditNotes: INITIAL_STATE.travelExpense.auditNotes,
    };

    if (!isEqual(travelExpenseToCompare, INITIAL_STATE.travelExpense)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.travelExpense.legalEntity &&
      !(state.travelExpense.acceptedAmount > state.travelExpense.amount) &&
      !(state.travelExpense.vatAmount > state.travelExpense.amount)
    );
  }, [state.travelExpense]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.travelExpense, areFieldsValidated]);

  const updateAuditDoc = useCallback((document: DocumentDTO | null) => {
    setState((state) => ({
      ...state,
      travelExpense: {
        ...state.travelExpense,
        auditDocument: document,
      },
      auditActionsMutated: true,
    }));
  }, []);

  const updateLastAuditNote = useCallback((auditNote: TravelExpenseAuditNotesDTO) => {
    !!auditNote &&
      setState((state) => ({
        ...state,
        travelExpense: {
          ...state.travelExpense,
          auditNotes: [auditNote],
        },
        auditActionsMutated: true,
      }));
  }, []);

  return (
    <>
      <Grid
        onClick={() => {
          setState((state) => ({ ...state, isDialogOpen: true }));
        }}
        sx={{
          "&:hover": {
            cursor: "pointer",
          },
        }}
      >
        {children}
      </Grid>

      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
        maxWidth="lg"
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={`${t(`${tRP}.audit_comments.expense_audit_report`)}`}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <Grid style={{ overflow: "auto", height: "65vh" }}>
              <AuditCommentsBody
                readOnly={readOnly}
                travelExpense={state.travelExpense}
                employee={employee}
                updateAuditDoc={updateAuditDoc}
                updateLastAuditNote={updateLastAuditNote}
                defaultTravelExpense={item}
                handleChangeField={handleChangeField}
                handleUpdateExportAuditNotes={handleUpdateExportAuditNotes}
              />
            </Grid>
          </DialogContent>
          <DialogActions
            handleSave={() => handleSave()}
            disabledSaveButton={!areFieldsValidated() || readOnly}
            handleCancel={resetAndCloseDialog}
            error={state.error}
          >
            {!readOnly && (
              <>
                <Button
                  onClick={() => handleSave(false)}
                  style={{
                    backgroundColor:
                      !areFieldsValidated() || state.travelExpense.auditAccepted === false
                        ? lighten(theme.palette.secondary.light, 0.6)
                        : theme.palette.warning.main,
                  }}
                  variant={"contained"}
                  disabled={!areFieldsValidated() || readOnly || state.travelExpense.auditAccepted === false}
                >
                  {`${t(`${tRP}.save_and_reject`)}`}
                </Button>

                <Button
                  onClick={() => handleSave(true)}
                  style={{
                    backgroundColor:
                      !areFieldsValidated() || readOnly || !!state.travelExpense.auditAccepted
                        ? lighten(theme.palette.secondary.light, 0.6)
                        : theme.palette.success.main,
                  }}
                  variant={"contained"}
                  disabled={!areFieldsValidated() || readOnly || !!state.travelExpense.auditAccepted}
                >
                  {`${t(`${tRP}.save_and_accept`)}`}
                </Button>
              </>
            )}
          </DialogActions>
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AuditComments;
